import _ from 'underscore';
import jQuery from 'jquery';
import Backbone from 'backbone';
import Api from '@/lib/api/api';

/**
 * Take a v5Model or an object with attrs like 'id' and 'type'
 * and return them. Useful for passing essential identifier data to API.
 * @param v5_Model or Object
 * @return Object
 */
function make_reference(model) {
    var attrs;
    if (model.toJsonApi) {
        attrs = model.toJsonApi();
    } else if (model.id && model.type) {
        attrs = model;
    }
    return _(attrs).pick('id', 'type');
}
/**
 * Given a raw relationship, eg.
 * { type: 'account', id: '<id>' }
 * Try to find it in a global collection or create it
 */
function get_rel_from_collection(rel) {
    if ( !rel || !rel.type) return;
    var coll = api_channel.request(rel.type);
    if (coll) {
        var obj = coll.get(rel.id);
        if (!obj) obj = coll.add(rel);
        return obj;
    }
    return rel;
}
function create_rel(parent_model) {
    var m = new Backbone.Model();
    function _all(func_name) {
        return _.chain(m.attributes)
            // Get each relationship
            .values()
            // Omit those which are not truthy
            .compact()
            // get the fetch functions for each model
            .map(function(model) {
                return _.bind(model[func_name], model);
            })
            // Build a promise chain out of the fetch functions
            .reduce(function(acc, func){
                return acc.then(func());
            }, jQuery.Deferred().resolve())
            .value();
    }
    // Use to fetch all the relationships
    m.fetch_all = _.partial(_all, 'fetch');
    m.load_all = _.partial(_all, 'load');
    
    parent_model.listenTo(m, 'change', function(rel, options) {
        return parent_model.trigger('change:rel', parent_model, rel, options);
    });
    return m;
}
var api_channel = Api.getChannel();
var v5_Model = Backbone.Model.extend({
    constructor: function(attributes, options) {
        //
        // Create a relationship model for each model
        //
        // A relationship model holds the relationships sent
        // back from the server. It uses the same lookup mechanisms as
        // the older model.<relationship> paradigm. Now it is a Model.
        //
        // Listen to the rel model for changes to the relationship.
        //
        this.rel = create_rel(this);
        return Backbone.Model.apply(this, arguments);
    },
    /** 
     * If the function looks like it has been synced at least once,
     * load simply returns with a resolved jquery Deferred object.
     * Otherwise, load fetches the object and returns the jQuery
     * xhr (Deferred) object.
     */
    load: function(options) {
        if (this.is_loaded()) {
            return jQuery.Deferred().resolve();
        }
        return this.fetch(options);
    },
    /**
     * Boolean result tells us if this model is considered 'loaded'.
     * Override this if you need to do something different.
     */
    is_loaded: function() {
        // Every object in the v5 API has a created_at attribute
        return !!this.get('created_at');
    },
    parse: Api.parse_item,
    toJsonApi: function() {
        var attrs;
        if (!this.raw_jsonapi) {
            // This has never been 'set' from a json api object.
            // We'll do our best to cobble it together.
            attrs = {
                type: this.type,
                attributes: _.extend({}, this.attributes)
            };
            attrs.id = attrs.attributes.id;
            delete attrs.attributes.id;
        } else {
            // Stringifying and parsing here effectively performs
            // a deep clone of the raw_jsonapi object we created in
            // our custom 'set' function, without us having to
            // worry about the references we created.
            // This reconstructs the model in the format that is
            // acceptible for json api.
            attrs = JSON.parse(JSON.stringify(this.raw_jsonapi));
            if (attrs.attributes && attrs.attributes.id) {
                delete attrs.attributes.id;
            }
        }
        return attrs;
    },
    toReference: function() {
        return make_reference(this);
    },
    sync: function(method, model, options) {
        options = (options ? options : {});
        options.headers =  _.extend({
            'Content-type': 'application/vnd.api+json'
        }, options.headers);
        if (!options.attrs) {
            var jsonapi = model.toJsonApi && model.toJsonApi();
            if (jsonapi) options.attrs = { data: jsonapi };
        }
        return Api.sync(method, model, options);
    },
    set: function(key, val, options) {
        if (!key) return this;

        // Handle both `"key", value` and `{key: value}` -style arguments.
        var attrs;
        if (typeof key === 'object') {
            attrs = key;
            options = val;
        } else {
            (attrs = {})[key] = val;
        }

        if (!options) options = {};
        options = _({ set_relationships: true }).extend(options);

        // Store the json api attributes for later use. We want to retain the
        // original structure of the object as sent from the server, but we
        // also want it to remain dynamic if attributes change in the client.
        if ('type' in attrs) this.type = attrs.type;
        if (this.idAttribute in attrs) this.id = attrs[this.idAttribute];
        if (attrs.included) {
            attrs.included.forEach(function(item) {
                api_channel.request(item.type, item);
            });
        }
        if ('attributes' in attrs || 'type' in attrs || 'relationships' in attrs || 'billing_parameters' in attrs) {
            this.raw_jsonapi = attrs;
            if (('relationships' in attrs)) {
                this.set_relationships(attrs.relationships);
            }
            if (('billing_parameters' in attrs)) {
                this.billing_parameters = attrs.billing_parameters;
            }
            // id is necessary to include so that collection.get will work
            attrs = _.extend({ id: attrs.id }, attrs.attributes);
            this.raw_jsonapi.attributes = this.attributes;
        }
        return Backbone.Model.prototype.set.call(this, attrs, options);
    },
    /**
     * Set relationships on this model from a Json-Api relationships object
     * Relationships are only set if the model specifies object keys
     * in the relationships array which is, by default, empty.
     */
    set_relationships: function(relationships) {
        // Populate this model's 'rel' with values from the API
        for (var key in relationships) {
            if (key === 'roles') {
                var roles  = [];
                _.each( relationships[key], function(r) {
                    roles.push(get_rel_from_collection(r));
                });
                this.rel.set(key, roles);
            } else {
                this.rel.set(key, get_rel_from_collection(relationships[key]));
            }
        }
        var missing = {};
        _.difference(
            Object.keys(this.rel.attributes), 
            Object.keys(relationships)
        ).forEach(function(key) {
            missing[key] = null;
        });
        this.rel.set(missing);
    }
});
v5_Model.make_reference = make_reference;
export default v5_Model;

